import type { QrCornerStyle, QrDotStyle } from "../types";

/** Quiet zone around the code, in modules. The spec asks for 4; 2 scans
 *  reliably on every phone camera and keeps the code from looking padded. */
export const QR_MARGIN = 2;

/** Share of the code's width the centre logo/icon covers, by default and at
 *  most. Error correction H recovers 30% of codewords; at 0.3 the cleared
 *  square is 9% of the area, still well inside that (scan-tested). */
export const QR_CENTER_DEFAULT = 0.22;
export const QR_CENTER_MIN = 0.12;
export const QR_CENTER_MAX = 0.3;

export type QrGeometry = {
  /** viewBox edge, in modules (code + both margins). */
  viewSize: number;
  dots: string;
  /** Finder- and alignment-pattern rings; drawn with fill-rule evenodd. */
  eyeFrames: string;
  eyeCenters: string;
  /**
   * Cleared square under the centre overlay, in viewBox units: whole modules,
   * so it grows in steps. `art` is the logo/icon box centred in it, sized
   * straight from the ratio so every step of the slider shows.
   */
  hole: { start: number; size: number; art: number } | null;
};

/** Clear space between the centre art and the nearest module, per side. */
const CENTER_GAP = 0.6;

const fmt = (value: number) => Number(value.toFixed(3));

const circle = (cx: number, cy: number, r: number) =>
  `M${fmt(cx - r)} ${fmt(cy)}a${fmt(r)} ${fmt(r)} 0 1 0 ${fmt(2 * r)} 0a${fmt(r)} ${fmt(r)} 0 1 0 ${fmt(-2 * r)} 0z`;

const roundedRect = (x: number, y: number, w: number, h: number, r: number) =>
  r <= 0
    ? `M${x} ${y}h${w}v${h}h${-w}z`
    : `M${fmt(x + r)} ${y}h${fmt(w - 2 * r)}a${r} ${r} 0 0 1 ${r} ${r}v${fmt(h - 2 * r)}a${r} ${r} 0 0 1 ${-r} ${r}h${fmt(-(w - 2 * r))}a${r} ${r} 0 0 1 ${-r} ${-r}v${fmt(-(h - 2 * r))}a${r} ${r} 0 0 1 ${r} ${-r}z`;

/** A unit square with each corner independently rounded (radius 0 or 0.5). */
const unitSquare = (
  x: number,
  y: number,
  [tl, tr, br, bl]: [number, number, number, number],
) =>
  `M${fmt(x + tl)} ${y}H${fmt(x + 1 - tr)}` +
  (tr ? `A${tr} ${tr} 0 0 1 ${x + 1} ${fmt(y + tr)}` : "") +
  `V${fmt(y + 1 - br)}` +
  (br ? `A${br} ${br} 0 0 1 ${fmt(x + 1 - br)} ${y + 1}` : "") +
  `H${fmt(x + bl)}` +
  (bl ? `A${bl} ${bl} 0 0 1 ${x} ${fmt(y + 1 - bl)}` : "") +
  `V${fmt(y + tl)}` +
  (tl ? `A${tl} ${tl} 0 0 1 ${fmt(x + tl)} ${y}` : "") +
  "z";

const eyeFrame = (x: number, y: number, style: QrCornerStyle) => {
  if (style === "circle") {
    return circle(x + 3.5, y + 3.5, 3.5) + circle(x + 3.5, y + 3.5, 2.5);
  }
  const [outer, inner] = style === "rounded" ? [2, 1.2] : [0, 0];
  return (
    roundedRect(x, y, 7, 7, outer) + roundedRect(x + 1, y + 1, 5, 5, inner)
  );
};

const eyeCenter = (x: number, y: number, style: QrCornerStyle) => {
  if (style === "circle") return circle(x + 3.5, y + 3.5, 1.5);
  return roundedRect(x + 2, y + 2, 3, 3, style === "rounded" ? 0.8 : 0);
};

/** A 5x5 alignment pattern whose top-left module is (x, y). */
const alignFrame = (x: number, y: number, style: QrCornerStyle) => {
  if (style === "circle") {
    return circle(x + 2.5, y + 2.5, 2.5) + circle(x + 2.5, y + 2.5, 1.5);
  }
  const [outer, inner] = style === "rounded" ? [1.4, 0.6] : [0, 0];
  return (
    roundedRect(x, y, 5, 5, outer) + roundedRect(x + 1, y + 1, 3, 3, inner)
  );
};

const alignCenter = (x: number, y: number, style: QrCornerStyle) =>
  style === "square"
    ? roundedRect(x + 2, y + 2, 1, 1, 0)
    : circle(x + 2.5, y + 2.5, 0.5);

/**
 * Centres of the alignment patterns for a matrix of size `n` (ISO 18004
 * table, computed as in Project Nayuki's reference encoder). Version 1 has
 * none; the three that would overlap a finder pattern are left out.
 */
const alignmentCenters = (n: number): [number, number][] => {
  const version = (n - 17) / 4;
  if (version < 2) return [];
  const count = Math.floor(version / 7) + 2;
  const step =
    version === 32 ? 26 : Math.ceil((version * 4 + 4) / (count * 2 - 2)) * 2;
  const positions = [6];
  for (let pos = n - 7; positions.length < count; pos -= step) {
    positions.splice(1, 0, pos);
  }
  const last = positions.length - 1;
  const centers: [number, number][] = [];
  positions.forEach((row, i) =>
    positions.forEach((col, j) => {
      const onFinder =
        (i === 0 && j === 0) ||
        (i === 0 && j === last) ||
        (i === last && j === 0);
      if (!onFinder) centers.push([row, col]);
    }),
  );
  return centers;
};

/**
 * Turns a module matrix into SVG paths: the data modules in the chosen dot
 * style, and the three finder patterns ("eyes") drawn separately so they can
 * take their own corner style. When `withCenter` is set, a square in the
 * middle is left empty for the logo/icon overlay.
 */
export const buildQrGeometry = (
  matrix: boolean[][],
  {
    dotStyle,
    cornerStyle,
    withCenter,
    centerRatio = QR_CENTER_DEFAULT,
  }: {
    dotStyle: QrDotStyle;
    cornerStyle: QrCornerStyle;
    withCenter: boolean;
    centerRatio?: number;
  },
): QrGeometry => {
  const n = matrix.length;
  const m = QR_MARGIN;

  // An odd hole stays centred on an odd matrix (every QR size is odd). The
  // hole moves in two-module steps; the art follows the ratio smoothly inside
  // it. round() lands at most half a module below n * ratio, so the art keeps
  // its gap at every ratio without clearing more than the scan-tested hole.
  let holeModules = 0;
  let artModules = 0;
  if (withCenter) {
    const ratio = Math.min(
      QR_CENTER_MAX,
      Math.max(QR_CENTER_MIN, centerRatio || QR_CENTER_DEFAULT),
    );
    holeModules = Math.round(n * ratio);
    if (holeModules % 2 === 0) holeModules += 1;
    artModules = n * ratio - 0.5 - 2 * CENTER_GAP;
  }
  const holeStart = (n - holeModules) / 2;

  const isFinder = (r: number, c: number) =>
    (r < 7 && c < 7) || (r < 7 && c >= n - 7) || (r >= n - 7 && c < 7);
  const isInHole = (r: number, c: number) =>
    holeModules > 0 &&
    r >= holeStart &&
    r < holeStart + holeModules &&
    c >= holeStart &&
    c < holeStart + holeModules;
  // Alignment patterns are drawn whole, like the finders: a scanner locates
  // them by their ring shape, which loose dots don't reproduce reliably. One
  // the logo would cut into is dropped; error correction H covers it.
  const holeEnd = holeStart + holeModules - 1;
  const alignments = alignmentCenters(n).filter(
    ([r, c]) =>
      !holeModules ||
      r + 2 < holeStart ||
      r - 2 > holeEnd ||
      c + 2 < holeStart ||
      c - 2 > holeEnd,
  );
  const isAlignment = (r: number, c: number) =>
    alignments.some(
      ([ar, ac]) => Math.abs(r - ar) <= 2 && Math.abs(c - ac) <= 2,
    );
  const isDot = (r: number, c: number) =>
    r >= 0 &&
    c >= 0 &&
    r < n &&
    c < n &&
    matrix[r][c] &&
    !isFinder(r, c) &&
    !isAlignment(r, c) &&
    !isInHole(r, c);

  let dots = "";
  for (let r = 0; r < n; r++) {
    if (dotStyle === "square") {
      // Merge each horizontal run into one rect: far fewer path commands.
      let c = 0;
      while (c < n) {
        if (!isDot(r, c)) {
          c++;
          continue;
        }
        const start = c;
        while (c < n && isDot(r, c)) c++;
        dots += `M${start + m} ${r + m}h${c - start}v1h${start - c}z`;
      }
      continue;
    }
    for (let c = 0; c < n; c++) {
      if (!isDot(r, c)) continue;
      if (dotStyle === "dots") {
        dots += circle(c + m + 0.5, r + m + 0.5, 0.45);
        continue;
      }
      // Rounded: round a corner only where both neighbours on that side are
      // empty, so runs join into smooth pills and lone modules become circles.
      const top = isDot(r - 1, c);
      const bottom = isDot(r + 1, c);
      const left = isDot(r, c - 1);
      const right = isDot(r, c + 1);
      const radius = 0.5;
      dots += unitSquare(c + m, r + m, [
        !top && !left ? radius : 0,
        !top && !right ? radius : 0,
        !bottom && !right ? radius : 0,
        !bottom && !left ? radius : 0,
      ]);
    }
  }

  const eyes: [number, number][] = [
    [m, m],
    [m + n - 7, m],
    [m, m + n - 7],
  ];

  return {
    viewSize: n + 2 * m,
    dots,
    eyeFrames:
      eyes.map(([x, y]) => eyeFrame(x, y, cornerStyle)).join("") +
      alignments
        .map(([r, c]) => alignFrame(c - 2 + m, r - 2 + m, cornerStyle))
        .join(""),
    eyeCenters:
      eyes.map(([x, y]) => eyeCenter(x, y, cornerStyle)).join("") +
      alignments
        .map(([r, c]) => alignCenter(c - 2 + m, r - 2 + m, cornerStyle))
        .join(""),
    hole: holeModules
      ? { start: holeStart + m, size: holeModules, art: artModules }
      : null,
  };
};
